import { GridColDef } from "@mui/x-data-grid";
import {
  DatePickerUncontrolled,
  Table,
  DateRangeSelect,
} from "@4uhub/lib4uhub";
import { useTranslation } from "react-i18next";

import { MainApi } from "../../services/mainApi.service";
import { CPFMask } from "../../utils/maks";
import { format } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";
import { Box, Stack } from "@mui/material";
import { useMemo, useState } from "react";

const LOG_REGISTER_ERRORS_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/Logs/GetLogRegisterError";

interface IRegisterError {
  id: string;
  establishmentId: string;
  document: string;
  errorMessage: string;
  created: string;
}

const logRegisterErrorService = new MainApi<IRegisterError>(
  LOG_REGISTER_ERRORS_ROUTE
);

const translationPath = "page.logs.logRegisterError.";

const LogRegisterErrors = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [enableFields, setEnableFields] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<Date | null>();

  const [startDate, setStartDate] = useState<Date | null>();

  const columns: GridColDef<IRegisterError>[] = [
    {
      field: "created",
      headerName: t(translationPath + "created") || "Created",
      flex: 1,
      maxWidth: 150,
      renderCell: (params) => {
        if (!params.value) return "-";

        const initialDate = new Date(params.value);
        return format(initialDate, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "errorMessage",
      headerName: t(translationPath + "errorMessage") || "Error Message",
      flex: 1,
    },
    {
      field: "document",
      headerName: t(translationPath + "document") || "Document",
      maxWidth: 300,
      valueFormatter(params) {
        return CPFMask.resolve(params.value);
      },
    },
  ];

  const props = useMemo(() => {
    return {
      StartDate: startDate ? startDate : undefined,
      EndDate: endDate ? endDate : undefined,
    };
  }, [startDate, endDate]);

  return (
    <Table<IRegisterError>
      showDefaultMacroFilters={false}
      service={logRegisterErrorService}
      columns={columns}
      title={t(translationPath + "title")}
      searchInput
      showButton={false}
      showDelete={false}
      serviceProps={props}
      showDeleteButton={false}
      defaultActions={false}
      macroFilters={() => {
        return (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: "0 0 200px" }}>
              <DateRangeSelect
                setEndDate={setEndDate}
                setStartDate={setStartDate}
                setEnableFields={setEnableFields}
              />
            </Box>

            <Box sx={{ flex: "0 0 200px" }}>
              <DatePickerUncontrolled
                value={startDate}
                onChange={setStartDate}
                label={`${t(translationPath + "start_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
            <Box sx={{ flex: "0 0 200px" }}>
              <DatePickerUncontrolled
                value={endDate}
                onChange={setEndDate}
                label={`${t(translationPath + "end_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
          </Stack>
        );
      }}
      searchInputPlaceHolder={t(translationPath + "placeholder")}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default LogRegisterErrors;
